import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const HERO_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_carpenter-pro-forte/artifacts/7m3u634f_WhatsApp%20Image%202026-09-13%20at%2007.28.27.webp";

export default function Hero() {
  const { t } = useLang();
  const h = t.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

  return (
    <section id="home" ref={ref} className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.15]" aria-hidden="true">
        <img src={HERO_IMG} alt={h.imgAlt} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0E11] via-[#0D0E11]/85 to-[#0D0E11]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D0E11] via-transparent to-[#0D0E11]/70" />
      </motion.div>
      <div className="absolute inset-0 bg-blueprint-grid" aria-hidden="true" />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-40 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-8 inline-flex items-center gap-3 border border-[#272B36] bg-[#14161C]/80 px-4 py-2 backdrop-blur"
        >
          <span className="h-2 w-2 animate-pulse-dot rounded-full bg-[#F97316]" />
          <span className="font-mono-tech text-[11px] font-medium tracking-[0.25em] text-[#94A3B8]">
            {h.badge}
          </span>
        </motion.div>

        <h1
          data-testid="hero-title"
          className="font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-[#F8FAFC] sm:text-7xl lg:text-8xl"
        >
          {h.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
              >
                {line.before}
                {line.accent && <span className="text-[#F97316]">{line.accent}</span>}
                {line.after}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#94A3B8] sm:text-lg"
        >
          {h.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-quote"
            onClick={() => scrollToId("#contatti")}
            className="group flex items-center gap-3 bg-[#E65100] px-8 py-4 font-mono-tech text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#F97316]"
          >
            {h.ctaQuote}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToId("#contatti")}
            className="border border-[#383E4E] px-8 py-4 font-mono-tech text-sm font-bold uppercase tracking-[0.15em] text-[#F8FAFC] transition-colors hover:border-[#F97316] hover:text-[#F97316]"
          >
            {h.ctaContact}
          </button>
        </motion.div>

        <motion.dl
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.05 }}
          className="mt-16 grid max-w-2xl grid-cols-1 gap-6 border-t border-[#272B36]/80 pt-8 sm:grid-cols-3"
        >
          {h.stats.map((s) => (
            <div key={s.label}>
              <dt className="font-mono-tech text-xl font-bold text-[#F97316]">{s.value}</dt>
              <dd className="mt-1 text-xs uppercase tracking-[0.15em] text-[#64748B]">{s.label}</dd>
            </div>
          ))}
        </motion.dl>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#chi-siamo")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-[#64748B] transition-colors hover:text-[#F97316]"
        aria-label={h.scrollAria}
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }} className="block">
          <ChevronDown className="h-6 w-6" />
        </motion.span>
      </motion.button>
    </section>
  );
}
